import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { isPaidPlanId } from '@/lib/planAccess';

function GateCard({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-900 md:p-6 lg:p-8">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200">{children}</div>
      </div>
    </div>
  );
}

/**
 * 스탠다드 이상 전용 페이지 게이트(무료 플랜 차단).
 * - 비로그인은 children 그대로(각 페이지의 기존 로그인 유도 화면 유지).
 * - subscription이 있으면 그 plan id로 판정. 재조회(refreshSubscription) 중에도 subscription은
 *   비워지지 않으므로 유료 본문이 언마운트되지 않는다 — isLoading만으로 스피너로 바꾸지 않는 이유.
 * - subscription이 없을 때만 로딩/조회 실패를 구분해 표시한다.
 */
export function PaidPlanGate({ message, children }: { message: string; children: ReactNode }) {
  const { user } = useAuth();
  const { subscription, isLoading, refreshSubscription } = useSubscription();

  if (!user) return <>{children}</>;

  if (subscription) {
    if (isPaidPlanId(subscription.subscription_plan_id)) return <>{children}</>;
    return (
      <GateCard>
        <Lock className="mx-auto mb-3 h-8 w-8 text-slate-400" aria-hidden="true" />
        <p className="break-keep text-sm font-medium text-slate-700">{message}</p>
        <Button asChild className="mt-5 min-h-11 bg-slate-900 text-white hover:bg-slate-800">
          <Link to="/mypage">요금제 보기</Link>
        </Button>
      </GateCard>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-16 w-16 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <GateCard>
      <p className="break-keep text-sm font-medium text-slate-700">구독 정보를 불러오지 못했습니다.</p>
      <Button
        className="mt-5 min-h-11 bg-slate-900 text-white hover:bg-slate-800"
        onClick={() => void refreshSubscription()}
      >
        다시 시도
      </Button>
    </GateCard>
  );
}
